/**
 * Mixin for Entity Compliance helper functions.
 * Houses formatting, UI, and type checks to keep the main component clean.
 */

const FILE_QUESTION_TYPES = ["file", "facial_verification"];
const MULTISELECT_QUESTION_TYPES = ["multi_select", "single_select", "checkboxes"];

const ANSWER_SOURCE_CLASSES = {
  compliance: "manually-verified",
  application: "pending",
  none: "unanswered",
  Submitted: "pending",
  Verified: "manually-verified",
  "Changes Requested": "text-danger-dark border-danger",
  submitted: "pending",
  verified: "manually-verified",
  changes_requested: "text-danger-dark border-danger",
};

const ANSWER_SOURCE_LABELS = {
  compliance: "Compliant",
  application: "Imported",
  none: "Unanswered",
  Submitted: "Submitted",
  Verified: "Verified",
  "Changes Requested": "Changes Requested",
  submitted: "Submitted",
  verified: "Verified",
  changes_requested: "Changes Requested",
};

const ANSWER_SOURCE_ICONS = {
  compliance: "bi bi-check-circle-fill",
  application: "bi bi-clock-fill",
  none: "",
  Submitted: "bi bi-send-fill",
  Verified: "bi bi-patch-check-fill",
  "Changes Requested": "bi bi-exclamation-triangle-fill",
  submitted: "bi bi-send-fill",
  verified: "bi bi-patch-check-fill",
  changes_requested: "bi bi-exclamation-triangle-fill",
};


import {
  get_headers,
  VERIFY_COMPLIANCE_ANSWER_URL
} from "@/utils/constants/constants";
import axios from "axios";

export default {
  data: () => ({
    // Verification state
    show_verification_modal: false,
    verifying_answer_id: null,
    verifying_status_id: null,
    verifying_status: "",
    verification_note: "",
    is_verifying: false,
  }),
  methods: {
    // ─── Formatting ──────────────────────────────────────────────

    format_datetime(date_string) {
      if (!date_string) return "—";
      const date = new Date(date_string);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    truncate_text(text, max_length) {
      const limit = max_length || 100;
      if (!text) return "";
      if (text.length > limit) {
        return text.substring(0, limit) + "...";
      }
      return text;
    },

    format_date(date_string) {
      if (!date_string) return "Not set";
      const date = new Date(date_string);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },

    // ─── UI & Badge Helpers ─────────────────────────────────────

    get_answer_source_badge_class(answer_source) {
      return Reflect.get(ANSWER_SOURCE_CLASSES, answer_source) || "bg-secondary";
    },

    get_answer_source_label(answer_source) {
      return Reflect.get(ANSWER_SOURCE_LABELS, answer_source) || "Unknown";
    },

    get_answer_source_icon(answer_source) {
      return Reflect.get(ANSWER_SOURCE_ICONS, answer_source) || ANSWER_SOURCE_ICONS.none;
    },

    // --- Verification Methods ---
    open_verification_modal(answer_id, status_or_id) {
      this.verifying_answer_id = answer_id;
      this.verification_note = "";
      this.verifying_status_id = null;
      this.verifying_status = "";

      if (typeof status_or_id === "number") {
        this.verifying_status_id = status_or_id;
      } else if (status_or_id) {
        this.verifying_status = String(status_or_id);
      }
      this.show_verification_modal = true;
    },
    close_verification_modal() {
      this.show_verification_modal = false;
      this.verifying_answer_id = null;
      this.verifying_status_id = null;
      this.verifying_status = "";
      this.verification_note = "";
    },
    async submit_verification() {
      this.is_verifying = true;
      const payload = {
        answer_id: this.verifying_answer_id,
        ...(this.verifying_status_id ? { status_id: this.verifying_status_id } : { status: this.verifying_status }),
        note: this.verification_note,
      };
      axios
        .post(VERIFY_COMPLIANCE_ANSWER_URL, payload, { headers: get_headers() })
        .then((response) => {
          if (response.data.status === "success") {
            this.close_verification_modal();
            if (this.load_compliance_overview) return this.load_compliance_overview();
          }
        })
        .catch((error) => {
          console.error("Error submitting verification:", error?.response?.data || error);
        })
        .finally(() => {
          this.is_verifying = false;
        });
    },

    get_progress_color_class(percentage) {
      if (percentage >= 80) return "progress-high";
      if (percentage >= 50) return "progress-mid";
      return "progress-low";
    },

    get_progress_bar_width_style(answered, total) {
      const width = total > 0 ? (answered / total) * 100 : 0;
      const style_object = { width: `${width}%` };
      return style_object;
    },

    get_answer_class(source) {
      if (!source) return "unanswered";
      const key = source;
      return Reflect.get(ANSWER_SOURCE_CLASSES, key) || "unanswered";
    },

    get_file_name_from_url(url) {
      if (!url) return "File";
      try {
        const urlObj = new URL(url);
        let name = urlObj.pathname.split('/').pop();
        if (name) return decodeURIComponent(name);
        return "File";
      } catch (e) {
        const url_without_params = url.split("?")[0];
        const url_parts = url_without_params.split("/");
        const last_part = url_parts[url_parts.length - 1];
        return last_part || "File";
      }
    },

    get_next_evaluation_class(date_string) {
      if (!date_string) return "text-muted";
      const next_date = new Date(date_string);
      const today = new Date();
      const days_until = Math.floor((next_date - today) / (1000 * 60 * 60 * 24));

      if (days_until < 0) return "text-danger";
      if (days_until <= 7) return "text-warning";
      return "text-success";
    },

    // ─── Question Type Checks ────────────────────────────────────

    is_file_type(question_type_raw) {
      const type_lower = (question_type_raw || "").toLowerCase();
      return FILE_QUESTION_TYPES.includes(type_lower);
    },

    is_multiselect_type(question_type_raw) {
      const type_lower = (question_type_raw || "").toLowerCase();
      return MULTISELECT_QUESTION_TYPES.includes(type_lower);
    },
  }
};
